<template>
    <div class="bg-white rounded-lg shadow flex-shrink-0 p-1">
        <div class="flex items-center justify-center gap-3">
            <button @click="moveToPreviousDate" class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                :disabled="isPreviousDisabled" :class="{ 'opacity-50 cursor-not-allowed': isPreviousDisabled }"
                aria-label="이전 날짜">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <div class="min-w-[250px] text-center">
                <Transition name="date-fade" mode="out-in">
                    <div :key="modelValue">
                        <span class="text-base text-gray-900">
                            {{ getDatePart(modelValue) }}
                        </span>
                        <span class="text-sm text-gray-500 ml-1">
                            {{ getLabelPart(modelValue) }}
                        </span>
                    </div>
                </Transition>
            </div>

            <button @click="moveToNextDate" class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                :disabled="isNextDisabled" :class="{ 'opacity-50 cursor-not-allowed': isNextDisabled }"
                aria-label="다음 날짜">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { Transition } from 'vue';
import { ref, computed } from 'vue';
import { getBusinessDays } from '@/utils/dateUtil';

export default {
    name: 'NavigationDateSelector',
    components: {
        Transition,
    },
    props: {
        modelValue: {
            type: String,
            required: true,
        },
    },
    emits: ['update:modelValue', 'dateSelected'],
    setup(props, { emit }) {
        const businessDays = ref(getBusinessDays(10));

        const getDatePart = (dateStr) => {
            const date = new Date(dateStr);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const dayOfWeek = new Intl.DateTimeFormat('ko-KR', {
                weekday: 'short',
            }).format(date);

            return `${month}/${day} (${dayOfWeek})`;
        };

        const getLabelPart = (dateStr) => {
            // 현재 선택된 날짜가 businessDays에서 몇 번째인지 확인
            const index = businessDays.value.findIndex(d => d === dateStr);

            if (index === 0) {
                return '';
            }
            return `(${index}영업일 전)`;
        };

        const currentIndex = computed(() => {
            return businessDays.value.findIndex(date => date === props.modelValue);
        });

        const isNextDisabled = computed(() => {
            return currentIndex.value <= 0;
        });

        const isPreviousDisabled = computed(() => {
            // 5영업일 전까지만 조회 가능 (index 4까지)
            return currentIndex.value >= businessDays.value.length - 1;
        });

        const moveToPreviousDate = () => {
            const nextIndex = currentIndex.value + 1;
            if (nextIndex < businessDays.value.length) {
                selectDate(businessDays.value[nextIndex]);
            }
        };

        const moveToNextDate = () => {
            const prevIndex = currentIndex.value - 1;
            if (prevIndex >= 0) {
                selectDate(businessDays.value[prevIndex]);
            }
        };

        const selectDate = (date) => {
            emit('update:modelValue', date);
            emit('dateSelected', date);
        };

        return {
            getDatePart,
            getLabelPart,
            moveToPreviousDate,
            moveToNextDate,
            isNextDisabled,
            isPreviousDisabled,
        };
    },
};
</script>

<style scoped>
.date-fade-enter-active,
.date-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.date-fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
}

.date-fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
}
</style>
